import { parseMarkdown } from '@octopus-do/octopus-editor'
import {
  Factory,
  type ID,
  type MonolithicBlock,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { getId } from '@store/utils'

import { COLORS_HEX } from '@/constants/colors'
import { isBlockWireframe } from '@/constants/wireframes'
import type { CrawledBlock } from '@/types'

const DEFAULT_BLOCK_TITLE = 'Untitled'

// The crawler names a palette colour; the project owns the matching ColorItem.
// No name, an unknown name or a colour missing from the project → default.
export const resolveBlockColorId = (
  state: AppState,
  color: string | null | undefined
): ID => {
  const hex = color ? COLORS_HEX[color] : null
  const projectColor = hex
    ? migrationProjectSelector.getProjectColorByHex(hex)(state)
    : null

  return (
    projectColor?.id || migrationProjectSelector.getProjectDefaultColorId(state)
  )
}

// The crawler's markdown becomes the block's editor value here, once, at
// import (OCT-5038). Empty markdown is no content at all.
const buildContent = (markdown: string | null | undefined) => {
  const value = markdown ? parseMarkdown(markdown) : []

  // The core types content as a string, the editor keeps a Plate value there
  // (see the todo in useMonolithicData); the same cast the editor modal makes.
  return value.length > 0 ? (value as unknown as string) : null
}

// Wireframe ids the catalog does not know are dropped: the block stays, only
// without a wireframe.
export const buildCrawledBlock = ({
  block,
  colorId,
}: {
  block: CrawledBlock
  colorId: ID
}): MonolithicBlock =>
  Factory.block.monolithic({
    id: getId(),
    colorId,
    title: block.title || DEFAULT_BLOCK_TITLE,
    content: buildContent(block.content),
    visual: Factory.block.wireframesVisual({
      wireframes: block.wireframes.filter(isBlockWireframe),
    }),
  })
